import React from "react";
import { Button, Divider, Typography, Popover, Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ChevronDownIcon as ArrowDropDown } from "lucide-react";
import clsx from "clsx";
import { CoachBriefInfoDto } from "@growth-machine-llc/stridist-api-client";
import ResetFiltersButton from "../exercise-library/filters/ResetFiltersButton";
import { CoachFilterContent } from "./CoachFilterContent";

const useStyles = makeStyles((theme) => ({
  root: {
    fontWeight: "bold",
    fontSize: 15,
  },
  active: {
    color: theme.palette.primary.main,
  },
  inactive: {
    color: theme.palette.text.primary,
  },
  paper: {
    borderRadius: theme.spacing(1.5),
    minWidth: theme.spacing(32),
    padding: theme.spacing(2, 0),
    margin: 0,
    overflowY: "hidden",
  },
  headerText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },
  header: {
    padding: theme.spacing(0, 1, 0.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

interface CoachFilterProps {
  selectedCoachIds: number[];
  onCoachesChange: (ids?: number[], allOptionsSelected?: boolean) => void;
  anchorEl?: HTMLElement | null;
  coaches: CoachBriefInfoDto[];
  className?: string;
  isClientTags?: boolean;
}

export function CoachFilter({
  selectedCoachIds = [],
  onCoachesChange,
  className,
  coaches = [],
}: CoachFilterProps) {
  const s = useStyles();
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);

  const allIsSelected =
    selectedCoachIds.length === coaches.length &&
    coaches.every((coach) => selectedCoachIds.includes(coach.id));

  const allCoachIds = React.useMemo(
    () => coaches.map((coach) => coach.id),
    [coaches],
  );

  React.useEffect(() => {
    if (
      (!selectedCoachIds || selectedCoachIds.length === 0) &&
      coaches.length > 0
    ) {
      handleAllToggle();
    }
  }, [selectedCoachIds, coaches]);

  const handleAllToggle = () => onCoachesChange(allCoachIds, allIsSelected);

  const coachFilterStatusText = React.useMemo(() => {
    const total = coaches.length;
    const selected = selectedCoachIds.length;

    return selected !== total ? `: ${selected}` : "";
  }, [selectedCoachIds, coaches]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  if (coaches.length === 0) return null;

  return (
    <>
      <Button
        ref={buttonRef}
        className={clsx(
          coachFilterStatusText ? s.active : s.inactive,
          s.root,
          className,
        )}
        onClick={handleOpen}
      >
        Coaches {coachFilterStatusText}
        <ArrowDropDown size={20} style={{ marginLeft: 2 }} />
      </Button>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={handleClose}
        classes={{ paper: s.paper }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box className={s.header}>
          <Typography className={clsx(s.headerText)}>
            Filter by Coaches
          </Typography>
          <ResetFiltersButton
            tooltipPlacement="bottom"
            disabled={allIsSelected}
            onClick={() => {
              handleAllToggle();
            }}
          />
        </Box>
        <Divider />
        <Box sx={{ overflowY: "auto", maxHeight: 450 }}>
          <CoachFilterContent
            selectedCoachIds={selectedCoachIds}
            onCoachesChange={onCoachesChange}
            coaches={coaches}
            allIsSelected={allIsSelected}
            onAllToggle={handleAllToggle}
          />
        </Box>
      </Popover>
    </>
  );
}
